import { memo, useRef } from "react";
import type { ID, ResourceResponse } from "types";
import { useTypedSelector, useResetState } from "hooks";
import { Filters, MenuItem, PaymentProduct } from "consts/openBanking";
import { TableGridMode } from "consts/table";
import { FilterVariant } from "consts/filters";
import { SetupIdentifier } from "consts/setups";
import { isFormChanged } from "util/forms";
import { RequestQueryMapper } from "util/request-query-mapper";
import { when } from "util/support";
import {
    useBankingSDKRecipientsActions,
    useBankingSDKConnectorsRecipientsPivotActions
} from "features/openBanking/actions";
import type {
    BankingSdkConnector,
    BankingSdkConnectorsRecipientsPivot
} from "features/openBanking/types";
import { getBankingSdkConnectorsRecipientsPivot } from "features/openBanking/api";
import { resetBankingSdkRecipientsState } from "features/openBanking/slice";
import { selectIsConstantsLoading, selectPaymentProducts } from "features/general/selectors";
import { selectDomains, selectIsSetupsLoading } from "features/setups/selectors";
import { withSx } from "ui/atoms/Modal";
import { TabLayout } from "ui/widgets/StatefulTabs";
import { useResourceContext } from "ui/widgets/Resource";
import { ManageOpenBankingResourcePivot } from "ui/organizms/OpenBanking/shared";
import { BankingSDKConnectorsRecipientsPivot } from "ui/forms/OpenBanking";
import BankingSDKConnectorsRecipientsPivotForm from "./BankingSDKConnectorsRecipientsPivotForm";
import { BankingSDKConnectorsRecipientsPivotFormContext } from "./BankingSDKConnectorsRecipientsPivotFormContext";

const ManageBankingSDKConnectorsRecipientsPivot = () => {
    const isConstantsLoading = useTypedSelector(selectIsConstantsLoading);
    const isSetupsLoading = useTypedSelector(selectIsSetupsLoading(SetupIdentifier.Mors));

    const { resource } = useResourceContext<BankingSdkConnector>();

    const {
        createBankingSDKConnectorsRecipientsPivot,
        deleteBankingSDKConnectorsRecipientsPivot
    } = useBankingSDKConnectorsRecipientsPivotActions();

    const {
        downloadCsv,
        getBankingSdkRecipients
    } = useBankingSDKRecipientsActions();

    const formBootstrapData = {
        MOR: useTypedSelector(selectDomains),
        PaymentProducts: useTypedSelector(selectPaymentProducts)
    };

    const formProps: BankingSDKConnectorsRecipientsPivot.BankingSDKConnectorsRecipientsPivotForm = {
        isBootstrapDataLoading: (
            isConstantsLoading ||
            isSetupsLoading
        ),
        bootstrapData: formBootstrapData,
        initialValues: BankingSDKConnectorsRecipientsPivot.getInitialValues({}, formBootstrapData),
        onSaveOrCreate: async () => { }
    };

    const form = BankingSDKConnectorsRecipientsPivot.useForm(formProps);

    const formRef = useRef(form);
    formRef.current = form;

    return (
        <TabLayout>
            <BankingSDKConnectorsRecipientsPivotFormContext.Provider
                value={{
                    ...form,
                    ...formProps
                }}
            >
                <ManageOpenBankingResourcePivot
                    <BankingSdkConnector, BankingSdkConnectorsRecipientsPivot>
                    TableProps={{
                        mode: TableGridMode.OpenBankingBankingSDKRecipients,
                        downloadCsv,
                        resetState: useResetState(resetBankingSdkRecipientsState),
                        fetchTableData: getBankingSdkRecipients,
                        filterTypes: new Map<FilterVariant, string[]>([
                            [FilterVariant.MultiOption, [
                                Filters.country,
                                Filters.accountCurrency
                            ]]
                        ])
                    }}
                    resource={resource}
                    menuItem={MenuItem.BankingSDKRecipients}
                    groupBy={({ recipientId }: BankingSdkConnectorsRecipientsPivot) => recipientId}
                    fetchPivots={({ pki }) => getBankingSdkConnectorsRecipientsPivot(
                        RequestQueryMapper.from()
                            .containsIn(Filters.bankId, `${pki}`)
                            .toString()
                    )}
                    AssociateActionProps={{
                        getOpenBankingResourcePivotActionProps: ({ id: recipientId }) => ({
                            ModalProps: {
                                titleSlot: 'Associate Banking SDK with Recipient',
                                DialogProps: withSx({
                                    width: '100%'
                                }),
                                children: (
                                    <BankingSDKConnectorsRecipientsPivotForm
                                        initialValues={{
                                            bankId: resource.pki,
                                            recipientId
                                        }}
                                    />
                                ),
                                onClose: () => formRef.current.resetForm()
                            },
                            TriggerComponentProps: {
                                TooltipProps: {
                                    title: `Associate Banking SDK with Recipient ${recipientId}`
                                }
                            },
                            ActionPanelProps: {
                                SaveActionProps: {
                                    disabled: !formRef.current.isValid || !isFormChanged(formRef.current),
                                },
                                onSave: ({ pki }) => {
                                    const { values } = formRef.current;

                                    const [request] = createBankingSDKConnectorsRecipientsPivot({
                                        isEnabled: values[BankingSDKConnectorsRecipientsPivot.FormField.IsEnabled],
                                        MOR: values[BankingSDKConnectorsRecipientsPivot.FormField.Mor],
                                        paymentProduct: Number(
                                            values[BankingSDKConnectorsRecipientsPivot.FormField.PaymentProduct]?.id ||
                                            PaymentProduct.Domestic
                                        ),
                                        regReportingCodes: when(
                                            values[BankingSDKConnectorsRecipientsPivot.FormField.RegReportingCodes],
                                            JSON.stringify(
                                                values[BankingSDKConnectorsRecipientsPivot.FormField.RegReportingCodes]
                                            ),
                                            undefined
                                        ),
                                        bankId: Number(resource.pki),
                                        recipientId: pki
                                    });

                                    return request as Promise<ResourceResponse<BankingSdkConnectorsRecipientsPivot>>;
                                }
                            }
                        })
                    }}
                    DissassociateActionProps={{
                        getOpenBankingResourcePivotActionProps: ({ pivotId }) => ({
                            ModalProps: {
                                titleSlot: 'Dissassociate Banking SDK from Recipient',
                                children: 'Do you want to delete Banking SDK Recipient Pivot ?'
                            },
                            TriggerComponentProps: {
                                TooltipProps: {
                                    title: `Delete Banking SDK Recipient Pivot ${pivotId}`
                                }
                            },
                            ActionPanelProps: {
                                onSave: ({ pki }) => {
                                    const [request] = deleteBankingSDKConnectorsRecipientsPivot({
                                        coreId: pki
                                    });

                                    return request as Promise<ResourceResponse<ID>>;
                                }
                            }
                        })
                    }}
                />
            </BankingSDKConnectorsRecipientsPivotFormContext.Provider>
        </TabLayout>
    );
};

export default memo(ManageBankingSDKConnectorsRecipientsPivot);
